"use client"

import { useQuery } from "@tanstack/react-query"
import { ArrowRight, RotateCcw } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { cn } from "@/common/lib/utils"
import AnimatedNumber from "@/modules/analytics/components/animated-number"
import { EVIDENCE_LADDER, resolvePolicy } from "@/modules/lernpfad/engine"
import type { PopulationSummary } from "@/modules/lernpfad/engine/aggregate"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { getPopulationSummary } from "@/lernpfad-population-client"

/**
 * What the current configuration does to the whole organisation, at the top of
 * the page and at a size worth reading.
 *
 * It used to be eleven-pixel text in a 320 px rail — the entire answer to "what
 * would that do?", rendered smaller than the controls that produce it. Figures
 * this size are the reason to change a setting at all, so they lead.
 *
 * The five are chosen to be non-redundant: two compliance measures, two cost
 * measures in different currencies (time and money), and one that says how much
 * of the record actually clears the bar the policy sets. A framework that moves
 * none of them has not done anything.
 */

interface Metric {
    key: string
    label: string
    before?: number
    after?: number
    format: (value: number) => string
    /**
     * Which direction is an improvement.
     *
     * `undefined` means genuinely neutral, and that is a real answer rather than
     * a missing one: more planned hours is not better or worse on its own — it
     * buys more development and costs more time. Colouring it green for "up"
     * would assert a preference the product does not hold.
     */
    direction?: "higher" | "lower"
}

/** Share of completions whose evidence reaches the policy's floor. */
function floorShare(summary: PopulationSummary, floor: string): number {
    const from = EVIDENCE_LADDER.indexOf(floor as (typeof EVIDENCE_LADDER)[number])
    const total = summary.kirkpatrick.completions || 1
    const clearing = EVIDENCE_LADDER.slice(Math.max(0, from)).reduce(
        (sum, instrument) => sum + summary.evidenceMix[instrument],
        0,
    )
    return (clearing / total) * 100
}

export default function HeroImpact({ state }: { state: LernpfadDemoState }) {
    const {
        t,
        localizer,
        committedConstraints,
        baselineConstraints,
        policy,
        overrideCount,
        resetPolicy,
    } = state

    const baseline = useQuery({
        queryKey: ["lernpfad-summary", baselineConstraints],
        queryFn: () => getPopulationSummary(baselineConstraints),
        staleTime: Infinity,
    })
    const tuned = useQuery({
        queryKey: ["lernpfad-summary", committedConstraints],
        queryFn: () => getPopulationSummary(committedConstraints),
        enabled: policy !== undefined,
        // Keep the previous answer on screen while the next one computes —
        // blanking the band on every slider release reads as a bug.
        placeholderData: (previous) => previous,
        staleTime: Infinity,
    })

    const before = baseline.data
    const after = policy ? tuned.data : baseline.data
    const floor = resolvePolicy(policy).evidence.floor
    const baseFloor = resolvePolicy(undefined).evidence.floor

    const metrics: Metric[] = [
        {
            key: "coverage",
            label: t("steuer.impact.coverage"),
            before: before && before.mandatoryCoverage * 100,
            after: after && after.mandatoryCoverage * 100,
            format: (value) => `${value.toFixed(1)} %`,
            direction: "higher",
        },
        {
            key: "overdue",
            label: t("steuer.impact.overdue"),
            before: before?.withOverdue,
            after: after?.withOverdue,
            format: (value) => localizer.num(Math.round(value)),
            direction: "lower",
        },
        {
            key: "hours",
            label: t("steuer.impact.hours"),
            before: before?.totalPlannedHours,
            after: after?.totalPlannedHours,
            format: (value) => localizer.num(Math.round(value)),
        },
        {
            key: "cost",
            label: t("steuer.impact.cost"),
            before: before?.totalCostUsd,
            after: after?.totalCostUsd,
            format: (value) => `USD ${localizer.num(Math.round(value))}`,
            direction: "lower",
        },
        {
            key: "evidence",
            label: t("steuer.impact.evidenceShare"),
            before: before && floorShare(before, baseFloor),
            after: after && floorShare(after, floor),
            format: (value) => `${Math.round(value)} %`,
            direction: "higher",
        },
    ]

    return (
        <section className="lp-card rounded-xl border bg-card">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
                <div className="flex items-baseline gap-2">
                    <h2 className="text-sm font-medium">{t("steuer.impact")}</h2>
                    <p className="text-xs text-muted-foreground">
                        {t("steuer.impact.population")} ·{" "}
                        {overrideCount > 0
                            ? t("steuer.overrides", { count: overrideCount })
                            : t("steuer.overrides.none")}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {policy && tuned.isFetching && (
                        <span className="text-xs text-muted-foreground">
                            {t("steuer.impact.loading")}…
                        </span>
                    )}
                    {overrideCount > 0 && (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={resetPolicy}
                            className="h-7 px-2 text-xs"
                        >
                            <RotateCcw className="size-3" />
                            {t("steuer.reset")}
                        </Button>
                    )}
                </div>
            </header>

            <dl className="grid grid-cols-2 divide-x divide-y md:grid-cols-3 xl:grid-cols-5 xl:divide-y-0">
                {metrics.map((metric) => (
                    <Figure key={metric.key} metric={metric} unchangedLabel={t("steuer.impact.same")} />
                ))}
            </dl>
        </section>
    )
}

function Figure({ metric, unchangedLabel }: { metric: Metric; unchangedLabel: string }) {
    const { before, after, format, direction } = metric
    const known = before !== undefined && after !== undefined
    const changed = known && Math.abs(after - before) > 0.05
    const improved =
        changed && direction !== undefined && (direction === "lower" ? after < before : after > before)
    const worsened =
        changed && direction !== undefined && (direction === "lower" ? after > before : after < before)

    return (
        <div className="min-w-0 px-4 py-3">
            <dt className="truncate text-xs text-muted-foreground">{metric.label}</dt>
            <dd className="mt-0.5">
                {after === undefined ? (
                    <span className="text-xl font-semibold text-muted-foreground">—</span>
                ) : (
                    <span
                        className="block text-xl font-semibold tabular-nums"
                        style={
                            improved
                                ? { color: "var(--lp-good)" }
                                : worsened
                                  ? { color: "var(--lp-warning)" }
                                  : undefined
                        }
                    >
                        <AnimatedNumber value={after} format={format} />
                    </span>
                )}
                {/* The previous value stays visible rather than being replaced:
                    a delta with no baseline is a number, not a comparison. */}
                <span
                    className={cn(
                        "mt-0.5 flex items-center gap-1 text-xs tabular-nums",
                        changed ? "text-muted-foreground" : "text-muted-foreground/60",
                    )}
                >
                    {changed && before !== undefined ? (
                        <>
                            <span className="line-through">{format(before)}</span>
                            <ArrowRight className="size-3" />
                        </>
                    ) : (
                        <span>{unchangedLabel}</span>
                    )}
                </span>
            </dd>
        </div>
    )
}
